/**
 * SubmitNdfForm — NDF Form Submission Page
 *
 * A clean, approachable form submission experience for NDF-format forms.
 * Uses the NdfRenderer component for interactive field blocks.
 *
 * Features:
 * - NdfRenderer with all fields interactive
 * - Validation on submit (leverages NdfRenderer's built-in validation)
 * - File upload support via onFileUpload callback
 * - Consent capture (email/SMS) from form settings
 * - Success message with optional redirect
 * - Mobile-first responsive design
 *
 * Aesthetic: "Friendly Form" — soft slate tones, generous spacing,
 * welcoming micro-interactions, clear call-to-actions.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  AlertTriangle,
  Send,
  FileText,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { Button, Card, CardContent, Checkbox } from '@respectly/ui';
import type { NexusDocument, NdfFieldBlock } from '@respectly/shared-types';
import { isFieldBlock } from '@respectly/shared-types';
import { normalizeToE164, executeRecaptcha } from '@respectly/utils';
import { PortalLayout } from '../layouts/PortalLayout';
import { NdfRenderer } from '../components/NdfRenderer';
import { NotFoundPage } from './NotFoundPage';
import { LoadingPage } from './LoadingPage';

// ─── Types ──────────────────────────────────────────────

interface FormBranding {
  logoUrl?: string | null;
  primaryColor?: string;
  logoPosition?: 'left' | 'center' | 'right';
  inputBorderRadius?: number;
  inputBorderColor?: string | null;
  inputBackgroundColor?: string | null;
  inputTextColor?: string | null;
  companyName?: string;
}

interface FormData {
  id: string;
  name: string;
  description?: string;
  ndfDocument: NexusDocument | null;
  branding?: FormBranding;
}

// ─── Constants ──────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// ─── Animation Variants ─────────────────────────────────

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const successVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

// ═══════════════════════════════════════════════════════
// VALIDATION HELPER
// ═══════════════════════════════════════════════════════

function validateFormFields(
  blocks: NexusDocument['blocks'],
  values: Record<string, unknown>
): Record<string, string> {
  const errors: Record<string, string> = {};

  blocks.forEach((block) => {
    if (!isFieldBlock(block)) return;

    const fieldBlock = block as NdfFieldBlock;
    const value = values[block.id];
    const validation = fieldBlock.validation;

    // Required check
    if (validation?.required) {
      const strValue = String(value ?? '').trim();
      if (!strValue) {
        errors[block.id] = validation.customMessage || `${fieldBlock.label} is required`;
        return;
      }
    }

    const strValue = String(value ?? '').trim();
    if (!strValue) return;

    // Length validation
    if (validation?.minLength && strValue.length < validation.minLength) {
      errors[block.id] = `Minimum ${validation.minLength} characters required`;
      return;
    }
    if (validation?.maxLength && strValue.length > validation.maxLength) {
      errors[block.id] = `Maximum ${validation.maxLength} characters allowed`;
      return;
    }

    // Number bounds
    if (fieldBlock.type === 'field_text' && fieldBlock.inputType === 'number') {
      const numValue = Number(strValue);
      if (validation?.min !== undefined && numValue < validation.min) {
        errors[block.id] = `Minimum value is ${validation.min}`;
        return;
      }
      if (validation?.max !== undefined && numValue > validation.max) {
        errors[block.id] = `Maximum value is ${validation.max}`;
        return;
      }
    }

    // Pattern validation
    if (validation?.pattern) {
      try {
        const regex = new RegExp(validation.pattern);
        if (!regex.test(strValue)) {
          errors[block.id] = validation.customMessage || 'Invalid format';
          return;
        }
      } catch {
        // Invalid regex, skip
      }
    }

    // Email validation
    if (fieldBlock.type === 'field_text' && fieldBlock.inputType === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(strValue)) {
        errors[block.id] = 'Please enter a valid email address';
        return;
      }
    }

    // Phone validation (libphonenumber-js — must parse as E.164 or a valid US national number)
    if (fieldBlock.type === 'field_text' && fieldBlock.inputType === 'phone') {
      if (!normalizeToE164(strValue).ok) {
        errors[block.id] = 'Please enter a valid phone number with country code (e.g. +1 555 123 4567)';
        return;
      }
    }
  });

  return errors;
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════

export function SubmitNdfForm() {
  // ─── Core State ─────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [notFound, setNotFound] = useState(false);

  // ─── Field Values & Errors ──────────────────────────
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ─── Consent State ──────────────────────────────────
  const [emailConsent, setEmailConsent] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);

  // ─── Submission State ───────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submissionId, setSubmissionId] = useState<string | null>(null);

  // ─── Refs ───────────────────────────────────────────
  const formRef = useRef<HTMLDivElement>(null);

  // ─── URL Params ─────────────────────────────────────
  const getPublicUrl = useCallback(() => {
    const pathParts = window.location.pathname.split('/');
    // Route: /form-ndf/:publicUrl
    return pathParts[pathParts.indexOf('form-ndf') + 1] || '';
  }, []);

  // ─── Fetch Form Data ────────────────────────────────
  useEffect(() => {
    const fetchForm = async () => {
      const publicUrl = getPublicUrl();

      if (!publicUrl) {
        setNotFound(true);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/public/form/${publicUrl}`);
        const result = await response.json();

        if (!response.ok || !result.success) {
          if (response.status === 404) {
            setNotFound(true);
          } else {
            setError(result.error?.message || 'Failed to load form');
          }
          setIsLoading(false);
          return;
        }

        // Check if this is an NDF form
        if (!result.data?.ndfDocument) {
          // Redirect to legacy form page
          window.location.href = `/form/${publicUrl}`;
          return;
        }

        setFormData(result.data);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching form:', err);
        setError('Failed to connect to server. Please try again.');
        setIsLoading(false);
      }
    };

    fetchForm();
  }, [getPublicUrl]);

  // ─── Form Settings ──────────────────────────────────
  const settings = useMemo(() => {
    return formData?.ndfDocument?.settings || {};
  }, [formData]);

  // ─── File Upload Handler ────────────────────────────
  const handleFileUpload = useCallback(
    async (file: File): Promise<string> => {
      const publicUrl = getPublicUrl();

      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await fetch(
        `${API_BASE}/public/form/${publicUrl}/upload`,
        {
          method: 'POST',
          body: uploadData,
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error?.message || 'Upload failed');
      }

      return result.data?.url || '';
    },
    [getPublicUrl]
  );

  // ─── Value Change Handler ───────────────────────────
  const handleChange = useCallback((fieldId: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));

    // Clear error when field changes
    setErrors((prev) => {
      if (prev[fieldId]) {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      }
      return prev;
    });
  }, []);

  // ─── Scroll to First Error ──────────────────────────
  const scrollToFirstError = useCallback((errorFields: Record<string, string>) => {
    const firstErrorId = Object.keys(errorFields)[0];
    if (!firstErrorId) return;

    // Find the element and scroll to it
    const element = document.getElementById(firstErrorId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus();
    }
  }, []);

  // ─── Submit Handler ─────────────────────────────────
  const handleSubmit = async () => {
    if (!formData?.ndfDocument) return;

    // Validate all fields
    const validationErrors = validateFormFields(
      formData.ndfDocument.blocks,
      values
    );

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      scrollToFirstError(validationErrors);
      return;
    }

    // Extract contact info from field values (email/phone/name text fields)
    const contactInfo: { email?: string; phone?: string; firstName?: string; lastName?: string } = {};
    for (const block of formData.ndfDocument.blocks) {
      if (!isFieldBlock(block)) continue;
      const fieldBlock = block as NdfFieldBlock;
      if (fieldBlock.type !== 'field_text') continue;
      const textBlock = fieldBlock as Extract<NdfFieldBlock, { type: 'field_text' }>;
      const value = String(values[block.id] ?? '').trim();
      if (!value) continue;
      if (textBlock.inputType === 'email') contactInfo.email = value;
      else if (textBlock.inputType === 'phone') contactInfo.phone = value;
      else if (textBlock.inputType === 'text') {
        const label = (textBlock.label || '').toLowerCase();
        if (label.includes('first') && label.includes('name')) contactInfo.firstName = value;
        else if (label.includes('last') && label.includes('name')) contactInfo.lastName = value;
        else if (label === 'name' || label === 'full name') {
          const parts = value.split(' ');
          contactInfo.firstName = parts[0];
          if (parts.length > 1) contactInfo.lastName = parts.slice(1).join(' ');
        }
      }
    }

    const publicUrl = getPublicUrl();
    setIsSubmitting(true);
    setErrors({});

    const recaptchaToken = await executeRecaptcha('FORM_SUBMIT');
    const requestBody = {
      data: values,
      contactInfo: Object.keys(contactInfo).length > 0 ? contactInfo : undefined,
      consents: {
        email: emailConsent,
        sms: smsConsent,
      },
      recaptchaToken,
    };

    try {
      const response = await fetch(
        `${API_BASE}/public/form/${publicUrl}/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        const errMsg = result.error?.message || result.message || `HTTP ${response.status}: Failed to submit form`;
        setError(errMsg);
        setIsSubmitting(false);
        return;
      }

      setSubmissionId(result.data?.submissionId || null);
      setIsSuccess(true);
      setIsSubmitting(false);
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit form. Please try again.');
      setIsSubmitting(false);
    }
  };

  // ─── Handle Redirect ────────────────────────────────
  const handleRedirect = () => {
    if (settings.redirectUrl) {
      window.location.href = settings.redirectUrl;
    }
  };

  // ═══════════════════════════════════════════════════════
  // RENDER STATES
  // ═══════════════════════════════════════════════════════

  if (isLoading) {
    return <LoadingPage message="Loading form..." />;
  }

  if (notFound) {
    return <NotFoundPage type="form" />;
  }

  // Branding colors (with defaults)
  const branding = formData?.branding || {};
  const primaryColor = branding.primaryColor || '#3b82f6';
  // Note: Input styling (inputBorderRadius, inputBorderColor, etc.) can be passed
  // to NdfRenderer in a future enhancement when it supports custom field styles

  if (error) {
    return (
      <PortalLayout showHeader={false}>
        <div className="relative flex flex-1 items-center justify-center p-4 overflow-hidden">
          {/* Layered gradient background */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${primaryColor}22 0%, ${primaryColor}08 35%, #ffffff 60%, ${primaryColor}05 100%)`,
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 80% 50% at 20% -10%, ${primaryColor}15 0%, transparent 60%)`,
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 60% 40% at 80% 110%, ${primaryColor}10 0%, transparent 50%)`,
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            }}
          />

          <Card className="relative z-10 w-full max-w-md border border-white/60 bg-white/80 backdrop-blur-xl shadow-[0_8px_32px_-8px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.08)] rounded-2xl overflow-hidden">
            <div
              className="h-1"
              style={{
                background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}99)`,
              }}
            />
            <CardContent className="p-8 text-center">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-50"
              >
                <AlertTriangle className="h-10 w-10 text-red-500" />
              </motion.div>
              <h1 className="text-xl font-semibold text-slate-900">
                Something went wrong
              </h1>
              <p className="mt-3 text-slate-600">{error}</p>
              <Button
                variant="outline"
                className="mt-6 border-slate-300"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  if (!formData || !formData.ndfDocument) {
    return <NotFoundPage type="form" />;
  }

  const ndfDoc = formData.ndfDocument;

  // ─── Success State ──────────────────────────────────
  if (isSuccess) {
    return (
      <PortalLayout showHeader={false}>
        <div className="relative flex flex-1 items-center justify-center p-4 overflow-hidden">
          {/* Layered gradient background */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${primaryColor}22 0%, ${primaryColor}08 35%, #ffffff 60%, ${primaryColor}05 100%)`,
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 80% 50% at 20% -10%, ${primaryColor}15 0%, transparent 60%)`,
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 60% 40% at 80% 110%, ${primaryColor}10 0%, transparent 50%)`,
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            }}
          />

          <motion.div
            variants={successVariants}
            initial="hidden"
            animate="visible"
            className="relative z-10 w-full max-w-lg"
          >
            {/* Glass-morphism card */}
            <Card className="overflow-hidden border border-white/60 bg-white/80 backdrop-blur-xl shadow-[0_8px_32px_-8px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.08)] rounded-2xl">
              {/* Accent bar */}
              <div
                className="h-1"
                style={{
                  background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}99)`,
                }}
              />

              <CardContent className="p-8 text-center sm:p-10">
                {/* Success icon */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.15, type: 'spring', stiffness: 200 }}
                  className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full"
                  style={{ backgroundColor: `${primaryColor}15` }}
                >
                  <CheckCircle2 className="h-10 w-10" style={{ color: primaryColor }} />
                </motion.div>

                {/* Success message */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                >
                  <h1 className="text-2xl font-semibold text-slate-900">
                    {settings.successMessage || 'Thank you for your submission!'}
                  </h1>

                  {submissionId && (
                    <p className="mt-3 text-sm text-slate-500">
                      Confirmation ID:{' '}
                      <span className="font-mono text-slate-700">
                        {submissionId.slice(0, 8).toUpperCase()}
                      </span>
                    </p>
                  )}
                </motion.div>

                {/* Action buttons */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="mt-8 space-y-3"
                >
                  {settings.redirectUrl ? (
                    <Button
                      variant="default"
                      className="w-full"
                      style={{
                        backgroundColor: primaryColor,
                        borderColor: primaryColor,
                      }}
                      onClick={handleRedirect}
                    >
                      Continue
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full border-slate-300 hover:bg-slate-50"
                      onClick={() => window.close()}
                    >
                      Close this page
                    </Button>
                  )}
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </PortalLayout>
    );
  }

  // ═══════════════════════════════════════════════════════
  // MAIN FORM VIEW
  // ═══════════════════════════════════════════════════════

  const showEmailConsent = settings.captureEmailConsent;
  const showSmsConsent = settings.captureSmsConsent;
  const showConsent = showEmailConsent || showSmsConsent;

  return (
    <PortalLayout showHeader={false}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="flex flex-1 flex-col"
      >
        {/* ─── Layered gradient background ───────────────── */}
        <div className="relative flex-1 overflow-y-auto">
          {/* Base gradient layer */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${primaryColor}22 0%, ${primaryColor}08 35%, #ffffff 60%, ${primaryColor}05 100%)`,
            }}
          />
          {/* Radial accent - creates depth */}
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 80% 50% at 20% -10%, ${primaryColor}15 0%, transparent 60%)`,
            }}
          />
          {/* Subtle bottom accent */}
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 60% 40% at 80% 110%, ${primaryColor}10 0%, transparent 50%)`,
            }}
          />
          {/* Subtle noise texture overlay for premium feel */}
          <div
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            }}
          />

          {/* Content */}
          <div className="relative z-10 mx-auto max-w-2xl px-3 py-6 sm:px-6 sm:py-12">
            {/* Form Header */}
            <motion.div variants={itemVariants} className="mb-8 text-center">
              <div
                className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                <FileText className="h-7 w-7" style={{ color: primaryColor }} />
              </div>
              <h1 className="text-2xl font-semibold text-slate-900 sm:text-3xl">
                {ndfDoc.meta.title}
              </h1>
              {ndfDoc.meta.description && (
                <p className="mx-auto mt-3 max-w-lg text-slate-600">
                  {ndfDoc.meta.description}
                </p>
              )}
            </motion.div>

            {/* Form Body - Glass-morphism card */}
            <motion.div
              ref={formRef}
              variants={itemVariants}
              className="rounded-2xl border border-white/60 bg-white/80 backdrop-blur-xl shadow-[0_8px_32px_-8px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.08)] p-4 sm:p-8 overflow-hidden"
            >
              {/* Accent bar */}
              <div
                className="h-1 -mx-4 -mt-4 sm:-mx-8 sm:-mt-8 mb-6"
                style={{
                  background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}99)`,
                }}
              />
              {/* NDF Renderer */}
              <NdfRenderer
                document={ndfDoc}
                values={values}
                onChange={handleChange}
                errors={errors}
                readOnly={false}
                onFileUpload={handleFileUpload}
                branding={{
                  primaryColor,
                  inputBorderRadius: branding.inputBorderRadius,
                  inputBorderColor: branding.inputBorderColor,
                  inputBackgroundColor: branding.inputBackgroundColor,
                  inputTextColor: branding.inputTextColor,
                }}
              />

              {/* Consent Section */}
              {showConsent && (
                <motion.div
                  variants={itemVariants}
                  className="mt-8 space-y-4 border-t border-slate-100 pt-6"
                >
                  <p className="text-sm font-medium text-slate-700">
                    Communication Preferences
                  </p>

                  {settings.consentText && (
                    <p className="text-sm text-slate-500">
                      {settings.consentText}
                    </p>
                  )}

                  <div className="space-y-3">
                    {showEmailConsent && (
                      <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border border-slate-200 p-3 transition-colors hover:bg-slate-50">
                        <Checkbox
                          checked={emailConsent}
                          onCheckedChange={(checked) =>
                            setEmailConsent(!!checked)
                          }
                        />
                        <span className="text-sm text-slate-700">
                          I agree to receive email communications
                        </span>
                      </label>
                    )}

                    {showSmsConsent && (
                      <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border border-slate-200 p-3 transition-colors hover:bg-slate-50">
                        <Checkbox
                          checked={smsConsent}
                          onCheckedChange={(checked) => setSmsConsent(!!checked)}
                        />
                        <span className="text-sm text-slate-700">
                          I agree to receive SMS messages
                        </span>
                      </label>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Submit Button */}
              <motion.div variants={itemVariants} className="mt-8">
                <Button
                  variant="default"
                  className="w-full py-3 text-base"
                  style={{
                    backgroundColor: primaryColor,
                    borderColor: primaryColor,
                  }}
                  size="lg"
                  disabled={isSubmitting}
                  onClick={handleSubmit}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      {settings.submitButtonText || 'Submit'}
                    </>
                  )}
                </Button>

                {/* Privacy note */}
                <p className="mt-4 text-center text-xs text-slate-400">
                  Your information is secure and encrypted
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </PortalLayout>
  );
}

export default SubmitNdfForm;
